import React, { useEffect, useState } from 'react';
import PageLayout from '../components/pagelayout';
import { supabase } from '../supabase';
import './navigationManagement.css';

const emptyLink = (type, sortOrder) => ({
  type,
  label_en: '',
  label_ar: '',
  url: '',
  is_visible: true,
  sort_order: sortOrder,
});

export default function NavigationManagement() {
  const [headerLinks, setHeaderLinks] = useState([]);
  const [footerLinks, setFooterLinks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingHeader, setSavingHeader] = useState(false);
  const [savingFooter, setSavingFooter] = useState(false);
  const [message, setMessage] = useState('');

  const loadLinks = async () => {
    setLoading(true);

    const { data, error } = await supabase
      .from('navigation_links')
      .select('*')
      .order('sort_order', { ascending: true });

    if (error) {
      console.error(error);
      setMessage('Failed to load navigation links.');
      setLoading(false);
      return;
    }

    setHeaderLinks(data.filter((link) => link.type === 'header'));
    setFooterLinks(data.filter((link) => link.type === 'footer'));
    setLoading(false);
  };

  useEffect(() => {
    loadLinks();

    const channel = supabase
      .channel('navigation_links_changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'navigation_links',
        },
        () => {
          loadLinks();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, []);

  const updateLink = (type, index, field, value) => {
    const setter = type === 'header' ? setHeaderLinks : setFooterLinks;

    setter((prev) =>
      prev.map((link, i) =>
        i === index ? { ...link, [field]: value } : link
      )
    );
  };

  const addLink = (type) => {
    if (type === 'header') {
      setHeaderLinks((prev) => [
        ...prev,
        emptyLink('header', prev.length + 1),
      ]);
    } else {
      setFooterLinks((prev) => [
        ...prev,
        emptyLink('footer', prev.length + 1),
      ]);
    }
  };

  const deleteLink = async (type, index) => {
    const links = type === 'header' ? headerLinks : footerLinks;
    const setter = type === 'header' ? setHeaderLinks : setFooterLinks;
    const link = links[index];

    setter((prev) => prev.filter((_, i) => i !== index));

    if (link.id) {
      const { error } = await supabase
        .from('navigation_links')
        .delete()
        .eq('id', link.id);

      if (error) {
        console.error(error);
        setMessage('Delete failed.');
        loadLinks();
      }
    }
  };

  const saveLinks = async (type) => {
    const links = type === 'header' ? headerLinks : footerLinks;
    const setSaving = type === 'header' ? setSavingHeader : setSavingFooter;
  
    setSaving(true);
    setMessage('');
  
    const existingLinks = links.filter((link) => link.id);
    const newLinks = links.filter((link) => !link.id);
  
    const updates = existingLinks.map((link, index) => ({
      id: link.id,
      type,
      label_en: link.label_en,
      label_ar: link.label_ar,
      url: link.url,
      is_visible: link.is_visible,
      sort_order: index + 1,
      updated_at: new Date().toISOString(),
    }));
  
    const inserts = newLinks.map((link, index) => ({
      type,
      label_en: link.label_en,
      label_ar: link.label_ar,
      url: link.url,
      is_visible: link.is_visible,
      sort_order: existingLinks.length + index + 1,
      updated_at: new Date().toISOString(),
    }));
  
    let updateError = null;
    let insertError = null;
  
    if (updates.length > 0) {
      const { error } = await supabase
        .from('navigation_links')
        .upsert(updates, { onConflict: 'id' });
  
      updateError = error;
    }
  
    if (inserts.length > 0) {
      const { error } = await supabase
        .from('navigation_links')
        .insert(inserts);
  
      insertError = error;
    }
  
    if (updateError || insertError) {
      console.error(updateError || insertError);
      setMessage('Save failed: ' + (updateError?.message || insertError?.message));
    } else {
      setMessage('Saved successfully.');
      loadLinks();
    }
  
    setSaving(false);
  };

  const renderLinkCard = (link, index, type) => (
    <div className="nav-link-card" key={link.id || index}>
      <div className="nav-link-row">
        <input
          className="nav-input nav-input-ar"
          value={link.label_ar}
          placeholder="العربية"
          dir="rtl"
          onChange={(e) =>
            updateLink(type, index, 'label_ar', e.target.value)
          }
        />

        <input
          className="nav-input"
          value={link.label_en}
          placeholder="English"
          onChange={(e) =>
            updateLink(type, index, 'label_en', e.target.value)
          }
        />

        <button
          className={`nav-icon-btn visible ${link.is_visible ? 'active' : ''}`}
          onClick={() =>
            updateLink(type, index, 'is_visible', !link.is_visible)
          }
          title="Toggle visibility"
        >
          {link.is_visible ? '👁' : '⊘'}
        </button>

        <button className="nav-icon-btn edit" title="Edit">
          ✎
        </button>

        <button
          className="nav-icon-btn delete"
          onClick={() => deleteLink(type, index)}
          title="Delete"
        >
          🗑
        </button>
      </div>

      <input
        className="nav-input nav-url-input"
        value={link.url}
        placeholder="/page-url"
        onChange={(e) => updateLink(type, index, 'url', e.target.value)}
      />
    </div>
  );

  return (
    <PageLayout activePage="navigation">
      <div className="navigation-page">
        <div className="navigation-header">
          <div>
            <h1>Navigation Management</h1>
            <p>Customize menu and footer links</p>
          </div>
        </div>

        {message && <div className="nav-message">{message}</div>}

        {loading ? (
          <div className="nav-loading">Loading navigation links...</div>
        ) : (
          <div className="navigation-grid">
            <section className="nav-panel">
              <div className="nav-panel-header">
                <h2>Header Navigation</h2>
                <button className="nav-add-btn" onClick={() => addLink('header')}>
                  + Add Link
                </button>
              </div>

              <div className="nav-list">
                {headerLinks.map((link, index) =>
                  renderLinkCard(link, index, 'header')
                )}
              </div>

              <button
                className="nav-save-btn"
                onClick={() => saveLinks('header')}
                disabled={savingHeader}
              >
                💾 {savingHeader ? 'Saving...' : 'Save Changes'}
              </button>
            </section>

            <section className="nav-panel">
              <div className="nav-panel-header">
                <h2>Footer Links</h2>
                <button className="nav-add-btn" onClick={() => addLink('footer')}>
                  + Add Link
                </button>
              </div>

              <div className="nav-list">
                {footerLinks.map((link, index) =>
                  renderLinkCard(link, index, 'footer')
                )}
              </div>

              <button
                className="nav-save-btn"
                onClick={() => saveLinks('footer')}
                disabled={savingFooter}
              >
                💾 {savingFooter ? 'Saving...' : 'Save Changes'}
              </button>
            </section>
          </div>
        )}
      </div>
    </PageLayout>
  );
}